import { useEffect, useState, type ReactNode } from "react";
import type { CustomerDevice } from "@apphq/shared/types";
import { EnvVatPeriodControls } from "../components/Controls";
import { PageHeader, useShell } from "../components/Shell";
import { AppIcon, Card, Chip, EmptyState, ErrorState, KpiCard, LoadingState, TableWrap } from "../components/ui";
import { PanelSkeleton, SkeletonRows } from "../components/skeletons";
import { getCustomer, getCustomers } from "../lib/api-admin";
import { sourceLabel } from "../lib/devices";
import { VAT_LABELS, countryNl, dateNl, dateTimeNl, eur, kindMeta, periodNl, relativeNl, statusMeta } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { Link, useLocation } from "../lib/router";
import { useAsync } from "../lib/useAsync";

export function Customers({ appUserId }: { appUserId: string | null }) {
  const { env } = usePrefs();
  const { apps } = useShell();
  const { search, setSearch } = useLocation();
  const q = search.get("q") ?? "";
  const appFilter = search.get("app_id") ?? "";
  const selectedApp = search.get("app_id") ?? "";

  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  // Debounce typing into the URL so every keystroke is not a request.
  useEffect(() => {
    if (draft === q) return;
    const t = setTimeout(() => setSearch({ q: draft.trim() || null }), 300);
    return () => clearTimeout(t);
  }, [draft, q, setSearch]);

  const list = useAsync(() => getCustomers({ q: q || undefined, app_id: appFilter || undefined, environment: env }), [q, appFilter, env]);
  const appName = (id: string) => {
    const a = apps.find((x) => x.id === id);
    return (a ? (a.display_name ?? a.name) : null) ?? id;
  };

  return (
    <>
      <PageHeader title="Klanten" subtitle="Alleen anonieme ID's: geen namen, geen e-mailadressen">
        <EnvVatPeriodControls showPeriod={false} />
      </PageHeader>

      <div className="split">
        <aside className="card split-list glass-soft">
          <input className="search search-block" type="search" placeholder="Zoek op klant- of transactie-ID" value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="Zoek klant" />
          <select className="select select-block" value={appFilter} onChange={(e) => setSearch({ app_id: e.target.value || null })} aria-label="Filter op app">
            <option value="">Alle apps</option>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>
                {a.display_name ?? a.name}
              </option>
            ))}
          </select>
          {list.status === "error" && <ErrorState message={list.error} onRetry={list.reload} />}
          {!list.data && list.status === "loading" && <SkeletonRows rows={8} />}
          {list.data && list.data.customers.length === 0 && <EmptyState title={q ? "Geen resultaten" : "Nog geen klanten"}>{q ? "Probeer een ander ID." : "Klanten verschijnen zodra de eerste aankoop binnenkomt."}</EmptyState>}
          {list.data && (
            <ul className="customer-list">
              {list.data.customers.map((c) => {
                const st = statusMeta(c.status);
                const active = c.app_user_id === appUserId && (!selectedApp || selectedApp === c.app_id);
                return (
                  <li key={`${c.app_id}:${c.app_user_id}`}>
                    <Link to={`/klanten/${encodeURIComponent(c.app_user_id)}?app_id=${encodeURIComponent(c.app_id)}${q ? `&q=${encodeURIComponent(q)}` : ""}`} className={active ? "customer-item active" : "customer-item"}>
                      <AppIcon name={appName(c.app_id)} size={28} seed={c.app_id} />
                      <span className="customer-main">
                        <span className="mono">{c.app_user_id.slice(0, 13)}…</span>
                        <span className="muted">
                          {c.product_id ?? "geen product"} · {countryNl(c.country)}
                        </span>
                      </span>
                      <Chip tone={st.tone}>{st.label}</Chip>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <div className="split-detail">
          {appUserId ? <CustomerDetail appUserId={appUserId} appId={selectedApp} appName={appName} /> : <Card><EmptyState title="Kies een klant" icon="◉">Selecteer een klant in de lijst om de tijdlijn, entitlements en abonnementen te zien.</EmptyState></Card>}
        </div>
      </div>
    </>
  );
}

function CustomerDetail({ appUserId, appId, appName }: { appUserId: string; appId: string; appName: (id: string) => string }) {
  const { env, vat } = usePrefs();
  const [copied, setCopied] = useState(false);
  const s = useAsync(() => (appId ? getCustomer(appUserId, { app_id: appId, environment: env, vat }) : Promise.reject(new Error("Kies een klant uit de lijst (app ontbreekt in de link)."))), [appUserId, appId, env, vat]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(appUserId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  if (s.status === "error") return <ErrorState message={s.error} onRetry={s.reload} />;
  const d = s.data;
  if (!d) return <PanelSkeleton kpis={5} chart={false} rows={5} />;
  const active = d.customer.entitlements.length > 0;

  return (
    <div className={s.status === "loading" ? "fade" : undefined}>
      <Card>
        <div className="customer-head">
          <AppIcon name={appName(d.app_id)} size={44} seed={d.app_id} />
          <div className="customer-title">
            <div className="muted">{appName(d.app_id)}</div>
            <div className="mono customer-id">{d.app_user_id}</div>
            <div className="chips">
              {active ? <Chip tone="good">Pro actief</Chip> : <Chip>Geen actieve entitlement</Chip>}
              <Chip tone={d.environment === "production" ? "neutral" : "warn"}>{d.environment === "production" ? "Productie" : "Sandbox"}</Chip>
              {d.country && <Chip>{countryNl(d.country)}</Chip>}
            </div>
          </div>
          <button className="pill" onClick={copy}>
            {copied ? "Gekopieerd" : "Kopieer ID"}
          </button>
        </div>
      </Card>

      <div className="kpi-grid">
        <KpiCard label={`Totale omzet (${VAT_LABELS[vat].toLowerCase()})`} value={eur(d.kpis.total_revenue)} />
        <KpiCard label="Opbrengst (schatting)" value={eur(d.kpis.total_proceeds)} />
        <KpiCard label="Klant sinds" value={dateNl(d.customer_since)} />
        <KpiCard label="Volgende verlenging" value={d.kpis.next_renewal ? dateNl(d.kpis.next_renewal) : "—"} />
        <KpiCard label="Laatst actief" value={relativeNl(d.last_seen_at)} />
      </div>

      <div className="grid-2">
        <Card title="Entitlements">
          {d.customer.entitlements.length === 0 ? (
            <EmptyState title="Geen actieve entitlements" />
          ) : (
            <ul className="plain-list">
              {d.customer.entitlements.map((e) => (
                <li key={`${e.id}:${e.product_id}`}>
                  <Chip tone="good">{e.id}</Chip> <span className="mono">{e.product_id}</span>
                  <span className="muted"> · {e.expires_at ? `tot ${dateNl(e.expires_at)}` : "levenslang"}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Abonnementen">
          {d.subscriptions.length === 0 ? (
            <EmptyState title="Geen abonnementen" />
          ) : (
            <ul className="plain-list">
              {d.subscriptions.map((sub) => {
                const st = statusMeta(sub.status);
                return (
                  <li key={sub.id}>
                    <Chip tone={st.tone}>{st.label}</Chip> <span className="mono">{sub.product_id}</span>
                    <span className="muted">
                      {" "}
                      · {periodNl(sub.period)} · {sub.auto_renew ? "verlengt automatisch" : "verlengt niet"} · t/m {dateNl(sub.current_period_end)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Tijdlijn">
        {d.timeline.length === 0 ? (
          <EmptyState title="Nog geen transacties" />
        ) : (
          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th>Tijd</th>
                  <th>Soort</th>
                  <th>Product</th>
                  <th>Land</th>
                  <th className="num">{VAT_LABELS[vat]}</th>
                  <th className="num">Opbrengst</th>
                </tr>
              </thead>
              <tbody>
                {d.timeline.map((t) => {
                  const k = kindMeta(t.kind);
                  return (
                    <tr key={t.id}>
                      <td>{dateTimeNl(t.occurred_at)}</td>
                      <td><Chip tone={k.tone}>{k.label}</Chip></td>
                      <td className="mono">{t.product_id}</td>
                      <td>{countryNl(t.country)}</td>
                      <td className="num">{eur(t.amount_eur)}</td>
                      <td className="num">{eur(t.proceeds_eur)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card title="Toestel & app">
        <DeviceBlock device={d.device} fallbackCountry={d.country} />
      </Card>
    </div>
  );
}

function DeviceBlock({ device, fallbackCountry }: { device: CustomerDevice; fallbackCountry: string | null }) {
  if (!device.available) {
    return (
      <EmptyState title="Nog geen toestel bekend" icon="▯">
        Zodra de app een heartbeat stuurt voor deze klant, verschijnen hier land, iOS-versie, appversie en of de app nog op de telefoon staat.
      </EmptyState>
    );
  }
  const country = device.country ?? fallbackCountry;
  const rows: Array<[string, ReactNode]> = [
    ["Op telefoon", device.on_phone ? <Chip tone="good">Ja, actief in de afgelopen 7 dagen</Chip> : <Chip tone="warn">Nee, al meer dan 7 dagen niet gezien</Chip>],
    ["Laatst gezien", `${relativeNl(device.last_seen_at)} (${dateTimeNl(device.last_seen_at)})`],
    ["Laatste teken van leven via", sourceLabel(device.last_source)],
    ["Land", countryNl(country)],
    ["iOS", device.os_version ?? "—"],
    ["Appversie", device.app_version ?? "—"],
  ];
  return (
    <dl className="facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="muted">{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
